/**
 * A Pinned tile: the live frame (task board, memory, a pin) filling its cell
 * of the Pinned rows, drawn through the frame's own component in the wall
 * tile renderer (`chrome: "none"`: its top row is a calm title line), on the
 * card's paper with its soft shadow. Nothing scrolls inside the tile: it
 * shows what fits, a pin's foot fades into the paper (the board and memory
 * end in their own lists), and the wheel scrolls the wall. A pin's handle is the hover pill, as on every card
 * (`WallCardActions`): Open · Pin · More; a click on the pin outside its own
 * controls opens it too. The Project's own board and memory
 * have none: their title line links to the full view. While a window (focus
 * or a floating frame) shows the frame, the tile keeps its place empty, so
 * one live copy runs at a time; a hidden wall runs none.
 */
import { memo, useRef, type ComponentType, type MouseEvent } from "react";
import { frameComponents } from "@/components/shapes";
import { SurfaceFade } from "@/components/ui";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { FrameRendererContext } from "@/components/shapes/shared/frame-renderer-context";
import { useFloatingFrameStore } from "@/desktop/floating-frame-store";
import { useFrameAnchor } from "@/hooks/use-frame-anchor";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { resolveCreatedSize } from "@/lib/frame-defaults";
import { useFocusVisibleHere } from "@/lib/focus/use-focus-visible-here";
import { useFrame } from "@/stores/frames-selectors";
import { usePresentationStore } from "@/stores/presentation-store";
import { useFramesStore } from "@/stores/frames-store";
import { useSelectionStore } from "@/stores/selection-store";
import { selectIsProjectTool } from "@/stores/wall-feed-store";
import { useWallViewportSize } from "./use-wall-viewport";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import { WallAgentTargetRing } from "./wall-card";
import { WallCardActions } from "./wall-card-actions";
import { wallCardPressHandlers } from "./wall-selection";
import { WALL_TILE_BODY_CLASS, WALL_TILE_CELL_CLASS } from "./wall-pin-tile";
import { wallTileFrameRenderer } from "./wall-tile-frame-renderer";

/**
 * Before its cell is measured, a tile shows its frame at its created size
 * (a tile is the live frame, not its opened window), up to this cap.
 */
const UNMEASURED_MAX_WIDTH = 720;
const UNMEASURED_MAX_HEIGHT = 460;

const FRAME_COMPONENTS: Partial<Record<string, ComponentType<FrameProps>>> =
  frameComponents;

/** A pin's own controls keep their click; anywhere else opens the pin. */
const PIN_CONTROL =
  "button, a, input, textarea, select, [contenteditable], [role='button'], [role='link']";

interface WallToolTileProps {
  id: string;
  /** The wall shows in this window; hidden, the tile runs no frame. */
  live: boolean;
  /** New work or onboarding's last scene: one ring pulse. */
  flashing: boolean;
  /** Open in focus over the wall. */
  opened: boolean;
  onOpen: (id: string) => void;
  onMenu: (id: string, anchor: ContextMenuAnchor) => void;
}

/** Whether a focus or floating window draws frame `id` in this window. */
function useShownInWindow(id: string): boolean {
  const focusHere = useFocusVisibleHere();
  const presented = usePresentationStore(
    (state) => state.presentation?.frameIds.includes(id) ?? false,
  );
  const floating = useFloatingFrameStore((state) => id in state.frames);
  return (focusHere && presented) || floating;
}

function WallToolTileContent({
  id,
  frame,
  live,
  flashing,
  opened,
  onOpen,
  onMenu,
}: WallToolTileProps & { frame: FrameData }) {
  const selected = useSelectionStore((state) =>
    state.multiSelectedFrameIds.includes(id),
  );
  const shownInWindow = useShownInWindow(id);
  const tool = useFramesStore((state) => selectIsProjectTool(state, id));
  // The workspace view adapter finds Pinned tiles through this anchor.
  const tileAnchorRef = useFrameAnchor(id, "tile");
  const bodyRef = useRef<HTMLDivElement>(null);
  const cell = useWallViewportSize(bodyRef);

  const Frame = FRAME_COMPONENTS[frame.frameType];
  const created = resolveCreatedSize(frame.frameType, frame);
  const width = cell.width || Math.min(created.w, UNMEASURED_MAX_WIDTH);
  const height = cell.height || Math.min(created.h, UNMEASURED_MAX_HEIGHT);
  const title = frameDisplayTitle(frame);
  const press = wallCardPressHandlers(id, onOpen, onMenu);
  const openPin = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest(PIN_CONTROL)) {
      return;
    }
    press.onClick(event);
  };

  return (
    <div
      ref={tileAnchorRef}
      className={WALL_TILE_CELL_CLASS}
      data-interactive="true"
      data-wall-tile=""
      data-testid={`wall-tool-tile-${id}`}
      data-frame-type={frame.frameType}
      data-selected={selected || undefined}
    >
      <div
        ref={bodyRef}
        className={cn(
          WALL_TILE_BODY_CLASS,
          (opened || selected) && "ring-2 ring-accent",
          flashing && "motion-safe:animate-wall-flash",
        )}
        data-testid="wall-tool-tile-body"
      >
        {Frame && live && !shownInWindow ? (
          // Its own stacking context keeps the frame's layers under the pill;
          // a frame taller than its row clips here (`wall-surface.css` stops
          // its inner scrollers too).
          <div
            className="wall-tile-content isolate h-full w-full overflow-hidden"
            onClick={tool ? undefined : openPin}
          >
            <FrameRendererContext.Provider value={wallTileFrameRenderer}>
              <Frame
                id={id}
                data={frame}
                selected={false}
                width={width}
                height={height}
              />
            </FrameRendererContext.Provider>
          </div>
        ) : (
          <div
            className="h-full w-full rounded-card bg-card-surface opacity-60"
            data-testid="wall-tool-tile-away"
          />
        )}
        {tool ? null : <SurfaceFade />}
        <WallAgentTargetRing id={id} />
        {tool ? null : (
          <WallCardActions
            id={id}
            title={title}
            selected={selected}
            onOpen={onOpen}
            onMenu={onMenu}
          />
        )}
      </div>
    </div>
  );
}

function WallToolTileBody(props: WallToolTileProps) {
  const frame = useFrame(props.id);
  if (!frame) return null;
  return <WallToolTileContent {...props} frame={frame} />;
}

/** A Pinned tile: re-renders only for its own frame (and its own props). */
export const WallToolTile = memo(WallToolTileBody);
